// [PURPOSE]: Pure helpers for armor + shield proficiency lookup. Centralizes the
// previously-inline hasProficiency logic from CharacterBasicsPanel.js:214 so
// effectCalculator + EquipmentPanel + spellCastingService can all share.
// [WHY]: Spec 1b axis A.6 — armor-proficiency penalty subsystem needs a SSOT
// for the proficiency check. Inline duplication = drift risk.

import effectRegistry from '../services/effectRegistry';

const ARMOR_TYPES = ['light', 'medium', 'heavy'];

// [SUBCLASS-PROF-BRIDGE 2026-06-13]: registry proficiency-grant key → bare armor
//   token. Lets the armor reader consult effectRegistry the way skills/saves already
//   do, so subclass-domain / feat / item armor grants (registered as `armor:<token>`
//   by useSubclassFeatureEffects et al. — but never written to the direct array)
//   reach this reader. Trace: 2026-06-13-datamodel-subclass-proficiency-grants-scp1.md.
const REGISTRY_ARMOR_TOKENS = [
  ['armor:light', 'light'],
  ['armor:medium', 'medium'],
  ['armor:heavy', 'heavy'],
  ['armor:shield', 'shields'],
];

function normalize(s) {
  return typeof s === 'string' ? s.toLowerCase().trim() : s;
}

// [PURPOSE]: Get the character's armor proficiency types (lowercase).
// [WHY]: Helper used by hasArmorProficiency + UI components. Expands the
// 'All armor' special case from the legacy proficiency strings.
export function getCharacterArmorProficiencies(character) {
  const raw = character?.proficiencies?.armor || [];
  const set = new Set();
  raw.forEach(prof => {
    const norm = normalize(prof);
    if (norm === 'all armor') {
      ARMOR_TYPES.forEach(t => set.add(t));
    } else if (norm.includes('shield')) {
      // 'Shields' AND 'Shields (non-metal)' (Druid) → the single shield token.
      set.add('shields');
    } else {
      // [ARMOR-PROF-FORMAT fix]: SRD + custom data store long-form proficiency
      // strings ('Light armor', 'Light armor (non-metal)') — merged onto the
      // character verbatim by CharacterCreationWizard.js:741 — but armorPiece.armorType
      // is the bare token ('light'). Reduce long-form to the bare type so the
      // includes() check in hasArmorProficiency matches. Short-form inputs
      // ('light', legacy/test) pass straight through.
      const type = ARMOR_TYPES.find(t => norm.startsWith(t));
      set.add(type || norm);
    }
  });
  // [SUBCLASS-PROF-BRIDGE 2026-06-13]: union registry-granted armor proficiencies
  //   (subclass domains like Life/Tempest/War, feats, items). Idempotent — a race/
  //   class that already lists the token in the direct array just re-adds it to the
  //   Set. Without this, subclass armor grants reach no reader (proven RED, scp1).
  REGISTRY_ARMOR_TOKENS.forEach(([profKey, token]) => {
    if (effectRegistry.getGrantedProficiencyLevel(profKey, character) >= 1) {
      set.add(token);
    }
  });
  return Array.from(set);
}

// [PURPOSE]: Reduce a stored armor proficiency LABEL ('Light armor', 'Shields',
//   'Heavy armor (non-metal)') to its effectRegistry grant KEY
//   ('armor:light' | 'armor:medium' | 'armor:heavy' | 'armor:shield').
// [WHY]: MULTICLASS-PROF-APPLY (2026-06-14) needs to turn authored
//   multiclassProficiencies.armor display strings into the SAME key form the
//   readers above query via getGrantedProficiencyLevel. Kept HERE (the armor-token
//   SSOT) rather than duplicated, so the long-form→token reduction lives once.
//   It is the inverse-direction sibling of getCharacterArmorProficiencies' inline
//   reduction (which yields the bare token; this yields the registry key).
// [INTEGRATION]: consumed by utils/multiclassProficiencyGrants.js.
//   Returns null for labels that don't map to a single key — notably 'All armor'
//   (never a valid multiclass grant per PHB p.163; callers drop nulls).
export function armorLabelToRegistryKey(label) {
  const norm = normalize(label);
  if (typeof norm !== 'string') return null;
  if (norm.includes('shield')) return 'armor:shield';
  const type = ARMOR_TYPES.find(t => norm.startsWith(t));
  return type ? `armor:${type}` : null;
}

// [PURPOSE]: Check if the character is proficient with the armor piece.
// [WHY]: Honors `armorPiece.proficiencyAs` override (e.g., Elven Chain counts as
// light even though it's medium). Returns true defensively when armorPiece is
// missing — non-armor / unknown items aren't penalized.
export function hasArmorProficiency(character, armorPiece) {
  if (!armorPiece || !armorPiece.armorType) return true; // No armor = no check
  const required = normalize(armorPiece.proficiencyAs || armorPiece.armorType);
  if (required === 'unarmored' || required === 'shield') return true; // Shields handled separately
  const profs = getCharacterArmorProficiencies(character);
  return profs.includes(required);
}

// [PURPOSE]: Check if the character is proficient with shields.
// [WHY]: Shields are a separate proficiency from light/medium/heavy. PHB: monk
// 'All armor' DOES NOT include Shields.
export function hasShieldProficiency(character, shieldPiece) {
  if (!shieldPiece || normalize(shieldPiece.armorType) !== 'shield') return true;
  const raw = character?.proficiencies?.armor || [];
  // [ARMOR-PROF-FORMAT fix]: match 'Shields' AND 'Shields (non-metal)' (Druid).
  // 'All armor' contains no 'shield' substring → still excluded (PHB: All armor ≠ Shields).
  if (raw.some(p => normalize(p).includes('shield'))) return true;
  // [SUBCLASS-PROF-BRIDGE 2026-06-13]: registry-granted shield proficiency (e.g.
  //   College of Valor Bard's `armor:shield`). Same reader-side union as armor types.
  return effectRegistry.getGrantedProficiencyLevel('armor:shield', character) >= 1;
}

// [PURPOSE]: UI helper — decides whether the EquipmentPanel should render
// the non-proficient-armor warning banner. Combines the proficiency check
// with the settings flag and inventory state.
// [WHY]: Spec 1b Task 10. Pulled into the util so the UI test doesn't need
// to mount the full EquipmentPanel.
export function shouldShowNonProficientArmorBanner(character, settings) {
  if (!settings?.enableArmorProficiencyPenalties) return false;
  const inv = character?.inventory || [];
  const equippedArmor = inv.find(i => i.equipped && i.armorType && i.armorType !== 'shield' && i.armorType !== 'unarmored');
  if (equippedArmor && !hasArmorProficiency(character, equippedArmor)) return true;
  const equippedShield = inv.find(i => i.equipped && i.armorType === 'shield');
  if (equippedShield && !hasShieldProficiency(character, equippedShield)) return true;
  return false;
}
